import { useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, Database, ArrowRightLeft } from "lucide-react";

/**
 * DataSync — Export/Import all business data as JSON.
 * Use case: sync data between Preview and Production environments.
 */
export default function DataSync() {
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // counts preview from parsed file
  const [report, setReport] = useState(null);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `tasklink-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const counts = data?.counts || {};
      const total = Object.values(counts).reduce((s, n) => s + n, 0);
      toast.success(`Exportados ${total.toLocaleString()} registros`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al exportar datos");
    } finally {
      setExporting(false);
    }
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportFile(f);
    setReport(null);
    try {
      const txt = await f.text();
      const json = JSON.parse(txt);
      const counts = json?.counts || Object.fromEntries(
        Object.entries(json?.collections || {}).map(([k, v]) => [k, Array.isArray(v) ? v.length : 0])
      );
      setPreview({ counts, exported_at: json?.exported_at, exported_by: json?.exported_by, raw: json });
    } catch (err) {
      toast.error("Archivo JSON inválido");
      setImportFile(null);
      setPreview(null);
    }
  };

  const handleImport = async () => {
    if (!preview?.raw?.collections) {
      toast.error("Selecciona un archivo JSON válido");
      return;
    }
    setImporting(true);
    setReport(null);
    try {
      const { data } = await api.post("/admin/import", {
        mode: importMode,
        collections: preview.raw.collections,
      });
      setReport(data);
      toast.success("Importación completada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setImportFile(null);
    setPreview(null);
    setReport(null);
    setImportMode("merge");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Card data-testid="admin-datasync-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-heading text-lg font-bold tracking-tight">Sincronización de datos</div>
              <p className="text-sm text-muted-foreground max-w-xl mt-1">
                Exporta todos los datos como JSON y súbelos en otro ambiente
                (Preview ↔ Producción) para sincronizar usuarios, proyectos, propuestas y más.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="rounded-full"
              data-testid="admin-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Exportando…" : "Exportar JSON"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { resetImportDialog(); setImportOpen(true); }}
              className="rounded-full"
              data-testid="admin-import-open-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar JSON
            </Button>
          </div>
        </div>
      </CardContent>

      <Dialog
        open={importOpen}
        onOpenChange={(o) => { setImportOpen(o); if (!o) resetImportDialog(); }}
      >
        <DialogContent className="max-w-lg" data-testid="admin-import-dialog">
          <DialogHeader>
            <DialogTitle>Importar datos</DialogTitle>
            <DialogDescription>
              Sube un archivo JSON exportado desde otro ambiente. Elige cómo manejar los datos existentes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm">Archivo JSON</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={onFilePick}
                className="mt-2 block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                data-testid="admin-import-file"
              />
              {preview && (
                <div className="mt-3 p-3 rounded-md bg-muted/50 border text-xs space-y-1" data-testid="admin-import-preview">
                  {preview.exported_at && (
                    <div className="text-muted-foreground">
                      Exportado el {new Date(preview.exported_at).toLocaleString()}{" "}
                      {preview.exported_by ? `por ${preview.exported_by}` : ""}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                    {Object.entries(preview.counts).map(([k, n]) => (
                      <div key={k} className="flex items-center gap-1">
                        <Database className="w-3 h-3" />
                        <span className="font-medium">{n}</span>
                        <span className="text-muted-foreground">{k}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm">Modo de importación</Label>
              <RadioGroup value={importMode} onValueChange={setImportMode} className="mt-2 space-y-2">
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="merge" id="mode-merge" data-testid="admin-import-mode-merge" />
                  <div className="grid gap-0.5">
                    <Label htmlFor="mode-merge" className="text-sm font-medium cursor-pointer">Combinar (recomendado)</Label>
                    <p className="text-xs text-muted-foreground">Inserta solo registros nuevos. Los existentes se omiten.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="replace" id="mode-replace" data-testid="admin-import-mode-replace" />
                  <div className="grid gap-0.5">
                    <Label htmlFor="mode-replace" className="text-sm font-medium cursor-pointer">Reemplazar</Label>
                    <p className="text-xs text-destructive/80">Borra TODOS los registros de cada colección incluida y luego inserta. Irreversible.</p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {report && (
              <div className="p-3 rounded-md bg-muted/50 border text-xs space-y-1" data-testid="admin-import-report">
                <div className="font-medium mb-1">Resultado ({report.mode}):</div>
                {Object.entries(report.collections || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-muted-foreground">{k}</span>
                    <span>
                      {v.skipped_reason || v.reason
                        ? <span className="text-destructive">{v.reason || "omitido"}</span>
                        : `+${v.inserted ?? 0} nuevos${v.skipped != null ? `, ${v.skipped} omitidos` : ""}${v.deleted != null ? `, ${v.deleted} eliminados` : ""}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportOpen(false)}
              data-testid="admin-import-cancel"
            >
              Cerrar
            </Button>
            <Button
              onClick={handleImport}
              disabled={!preview || importing}
              data-testid="admin-import-confirm"
            >
              {importing ? "Importando…" : importMode === "replace" ? "Reemplazar datos" : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
